import React, { Suspense, useMemo } from 'react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls, Text, Grid, Environment, PerspectiveCamera } from '@react-three/drei';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import * as THREE from 'three';
import './ThreeDView.css';

// Helper function to create geometry from 2D shape data
function createCustomGeometry(shape) {
  if (shape.type !== 'rectangle' || !shape.sides) {
    return null; // Use default geometry for non-custom shapes
  }

  // Create shape from sides with curves
  const shapeGeometry = new THREE.Shape();
  let hasCustomGeometry = false;

  // Check if any side has curves
  const hasCurvedSides = shape.sides && shape.sides.some(side => 
    side.curved || side.curve || side.midControl
  );

  if (!hasCurvedSides) {
    return null; // Use default box geometry
  }

  hasCustomGeometry = true;

  // Define the corners of the rectangle
  const corners = [
    { x: 0, y: 0 }, // top-left
    { x: shape.width, y: 0 }, // top-right
    { x: shape.width, y: shape.height }, // bottom-right
    { x: 0, y: shape.height }, // bottom-left
  ];

  // Start at the first corner
  shapeGeometry.moveTo(corners[0].x / 100, corners[0].y / 100);

  // Draw each side (4 sides for rectangle)
  for (let i = 0; i < 4; i++) {
    const startCorner = corners[i];
    const endCorner = corners[(i + 1) % 4];
    const side = shape.sides && shape.sides[i];

    if (side && (side.curved || side.curve || side.midControl)) {
      // Draw curved side
      let controlPoint;
      
      if (side.midControl) {
        controlPoint = {
          x: side.midControl.x / 100,
          y: side.midControl.y / 100
        };
      } else if (side.curve && side.curve.controlPoint) {
        controlPoint = {
          x: side.curve.controlPoint.x / 100,
          y: side.curve.controlPoint.y / 100
        };
      } else {
        // Default control point at midpoint
        controlPoint = {
          x: (startCorner.x + endCorner.x) / 200,
          y: (startCorner.y + endCorner.y) / 200
        };
      }

      // Create quadratic curve
      shapeGeometry.quadraticCurveTo(
        controlPoint.x,
        controlPoint.y,
        endCorner.x / 100,
        endCorner.y / 100
      );
    } else {
      // Draw straight line
      shapeGeometry.lineTo(endCorner.x / 100, endCorner.y / 100);
    }
  }

  // Extrude the shape to create 3D geometry
  const extrudeSettings = {
    depth: 1,
    bevelEnabled: false
  };

  return new THREE.ExtrudeGeometry(shapeGeometry, extrudeSettings);
}

// Component to render individual 3D shapes with measurements
function Shape3D({ shape, index, isMergedStructure = false, mergedIndex = 0 }) {
  const position = useMemo(() => [
    (shape.x - 400) / 100, // Center and scale
    0.01, // Just above ground level for planes
    (shape.y - 300) / 100 // Center and scale
  ], [shape.x, shape.y]);

  // Special rotation for plane geometries to lay flat
  const getRotation = () => {
    if (shape.type === 'rectangle') {
      return [-Math.PI / 2, 0, 0]; // Rotate to lay flat on ground
    }
    return shape.rotation ? [0, shape.rotation, 0] : [0, 0, 0];
  };

  // Create custom geometry for shapes with curves
  const customGeometry = useMemo(() => {
    return createCustomGeometry(shape);
  }, [shape]);

  const triangleGeometry = useMemo(() => {
    if (shape.type === 'triangle') {
      // Create a triangle shape
      const triangleShape = new THREE.Shape();
      triangleShape.moveTo(0, 0);
      triangleShape.lineTo(shape.width / 100, 0);
      triangleShape.lineTo(shape.width / 200, shape.height / 100);
      triangleShape.lineTo(0, 0);

      // Extrude the shape
      const geometry = new THREE.ExtrudeGeometry(triangleShape, {
        depth: 1,
        bevelEnabled: false
      });

      return geometry;
    }
    return null;
  }, [shape]);

  const getShapeGeometry = () => {
    // Use custom geometry if available (for curved shapes)
    if (customGeometry) {
      return <primitive object={customGeometry} />;
    }

    switch (shape.type) {
      case 'rectangle':
        return (
          <planeGeometry
            args={[
              shape.width / 100, // width
              shape.height / 100 // height
            ]}
          />
        );
      
      case 'circle':
        return (
          <cylinderGeometry
            args={[
              shape.radius / 100, // top radius
              shape.radius / 100, // bottom radius
              1, // height
              32 // radial segments
            ]}
          />
        );
      
      case 'triangle':
        return <primitive object={triangleGeometry} />;
      
      case 'door':
        // Create a semi-circle door
        return (
          <cylinderGeometry
            args={[
              shape.radius / 100, // top radius
              shape.radius / 100, // bottom radius
              0.2, // height (thinner for door)
              16, // radial segments
              1, // height segments
              false, // open ended
              0, // theta start
              Math.PI // theta length (half circle)
            ]}
          />
        );
      
      default:
        return <boxGeometry args={[1, 1, 1]} />;
    }
  };

  const getShapeColor = () => {
    if (isMergedStructure) {
      // Different colors for merged structure shapes
      const mergedColors = ['#ff6b6b', '#4ecdc4', '#45b7d1', '#96ceb4', '#feca57', '#ff9ff3'];
      return mergedColors[mergedIndex % mergedColors.length];
    }
    
    const colors = {
      'rectangle': '#4facfe',
      'circle': '#fa709a',
      'triangle': '#a8edea',
      'door': '#ffd700',
      'line': '#43e97b'
    };
    return colors[shape.type] || '#cccccc';
  };

  // Calculate dimensions for display
  const getDimensions = () => {
    switch (shape.type) {
      case 'rectangle':
        return `${Math.round(shape.width)}×${Math.round(shape.height)}`;
      case 'circle':
        return `⌀${Math.round(shape.radius * 2)}`;
      case 'triangle':
        return `${Math.round(shape.width)}×${Math.round(shape.height)}`;
      case 'door':
        return `⌀${Math.round(shape.radius * 2)}`;
      default:
        return '';
    }
  };

  // Determine if shape has custom geometry (curves)
  const hasCustomFeatures = () => {
    if (shape.sides && shape.sides.some(side => 
      side.curved || side.curve || side.midControl
    )) {
      return '(Custom)';
    }
    if (shape.isCurveMode || shape.controlPoint) {
      return '(Curved)';
    }
    return '';
  };

  return (
    <group>
      <mesh position={position} rotation={getRotation()} castShadow>
        {getShapeGeometry()}
        <meshStandardMaterial
          color={getShapeColor()}
          transparent
          opacity={shape.type === 'door' ? 0.7 : 0.8}
          roughness={0.3}
          metalness={0.1}
        />
      </mesh>
      
      {/* Shape label with golden color */}
      <Text
        position={[position[0], 1.5, position[2]]}
        fontSize={0.28}
        color="#FFD700" // Golden color
        anchorX="center"
        anchorY="middle"
        rotation={[-Math.PI / 6, 0, 0]}
        outlineWidth={0.02}
        outlineColor="#000000"
      >
        {isMergedStructure ? `Part ${mergedIndex + 1}` : shape.type.charAt(0).toUpperCase() + shape.type.slice(1)} {!isMergedStructure && (index + 1)}
      </Text>
      
      {/* Dimension label with bright yellow */}
      <Text
        position={[position[0], 1.0, position[2]]}
        fontSize={0.22}
        color="#FFFF00" // Bright yellow
        anchorX="center"
        anchorY="middle"
        rotation={[-Math.PI / 6, 0, 0]}
        outlineWidth={0.015}
        outlineColor="#000000"
      >
        {getDimensions()}
      </Text>

      {/* Custom features indicator */}
      {hasCustomFeatures() && (
        <Text
          position={[position[0], 0.5, position[2]]}
          fontSize={0.18}
          color="#FF8C00" // Dark orange
          anchorX="center"
          anchorY="middle"
          rotation={[-Math.PI / 6, 0, 0]}
          outlineWidth={0.01}
          outlineColor="#000000"
        >
          {hasCustomFeatures()}
        </Text>
      )}
    </group>
  );
}

// Component to render merged structures as unified 3D objects
function MergedStructure3D({ structure, index }) {
  // Calculate bounding box of the merged structure
  const bounds = useMemo(() => {
    return structure.shapes.reduce((acc, shape) => {
      const minX = Math.min(acc.minX, shape.x);
      const minY = Math.min(acc.minY, shape.y);
      const maxX = Math.max(acc.maxX, shape.x + (shape.width || shape.radius * 2 || 0));
      const maxY = Math.max(acc.maxY, shape.y + (shape.height || shape.radius * 2 || 0));
      return { minX, minY, maxX, maxY };
    }, { minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity });
  }, [structure.shapes]);

  const centerPosition = useMemo(() => [
    ((bounds.minX + bounds.maxX) / 2 - 400) / 100,
    0.5,
    ((bounds.minY + bounds.maxY) / 2 - 300) / 100
  ], [bounds]);

  const structureWidth = bounds.maxX - bounds.minX;
  const structureHeight = bounds.maxY - bounds.minY;

  // Count custom features in the structure
  const customFeaturesCount = useMemo(() => {
    return structure.shapes.filter(shape => 
      (shape.sides && shape.sides.some(side => side.curved || side.curve || side.midControl)) ||
      shape.isCurveMode || shape.controlPoint
    ).length;
  }, [structure.shapes]);

  return (
    <group>
      {/* Render individual shapes within the structure */}
      {structure.shapes.map((shape, shapeIndex) => (
        <Shape3D 
          key={shape.id || shapeIndex} 
          shape={shape} 
          index={shapeIndex}
          isMergedStructure={true}
          mergedIndex={shapeIndex}
        />
      ))}
      
      {/* Structure outline/bounds indicator */}
      <mesh 
        position={centerPosition} 
        castShadow={false}
      >
        <boxGeometry args={[structureWidth / 100, 0.1, structureHeight / 100]} />
        <meshStandardMaterial 
          color="#ff4757" 
          transparent 
          opacity={0.2}
          wireframe={true}
        />
      </mesh>
      
      {/* Structure label with golden color */}
      <Text
        position={[centerPosition[0], 3.5, centerPosition[2]]}
        fontSize={0.32}
        color="#FFD700" // Golden color
        anchorX="center"
        anchorY="middle"
        rotation={[-Math.PI / 6, 0, 0]}
        outlineWidth={0.025}
        outlineColor="#000000"
      >
        Complex Room {index + 1}
      </Text>
      
      {/* Structure dimensions with bright colors */}
      <Text
        position={[centerPosition[0], 2.9, centerPosition[2]]}
        fontSize={0.25}
        color="#00FF7F" // Spring green
        anchorX="center"
        anchorY="middle"
        rotation={[-Math.PI / 6, 0, 0]}
        outlineWidth={0.02}
        outlineColor="#000000"
      >
        {Math.round(structureWidth)}×{Math.round(structureHeight)}
      </Text>

      {/* Shape count and custom features */}
      <Text
        position={[centerPosition[0], 2.3, centerPosition[2]]}
        fontSize={0.2}
        color="#FFA500" // Orange
        anchorX="center"
        anchorY="middle"
        rotation={[-Math.PI / 6, 0, 0]}
        outlineWidth={0.015}
        outlineColor="#000000"
      >
        {structure.shapes.length} shapes{customFeaturesCount > 0 ? ` • ${customFeaturesCount} custom` : ''}
      </Text>
    </group>
  );
}

// Ground plane component
function Ground() {
  return (
    <>
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -0.02, 0]} receiveShadow>
        <planeGeometry args={[30, 30]} />
        <meshStandardMaterial 
          color="#f5f5dc" // Beige floor color
          transparent 
          opacity={0.9}
          roughness={0.8}
          metalness={0.1}
        />
      </mesh>
      <Grid
        infiniteGrid
        size={1}
        cellSize={1}
        cellThickness={0.3}
        cellColor="#e0e0e0"
        sectionSize={5}
        sectionThickness={0.8}
        sectionColor="#d0d0d0"
        fadeDistance={25}
        fadeStrength={1}
      />
    </>
  );
}

// Loading component
function LoadingFallback() {
  return (
    <div className="loading-3d">
      <div className="loading-spinner"></div>
      <p>Loading 3D View...</p>
    </div>
  );
}

// Main 3D View component
function ThreeDView() {
  const navigate = useNavigate();
  const { shapes, mergedStructures } = useApp();

  // Filter individual shapes that are not part of merged structures
  const individualShapes = useMemo(() => {
    return shapes.filter(shape => {
      // Check if this shape is part of any merged structure
      const isPartOfMerged = mergedStructures.some(structure =>
        structure.shapes.some(mergedShape => mergedShape.id === shape.id)
      );
      
      // Only include shapes that are not part of merged structures and are renderable
      return !isPartOfMerged && ['rectangle', 'circle', 'triangle', 'door'].includes(shape.type);
    });
  }, [shapes, mergedStructures]);

  const totalShapes = individualShapes.length + mergedStructures.length;

  const handleBackToPlanner = () => {
    navigate('/planner');
  };

  const handleBackToWelcome = () => {
    navigate('/');
  };

  return (
    <div className="threed-view-container">
      {/* Header */}
      <div className="threed-header">
        <div className="header-left">
          <h1 className="threed-title">
            <span className="title-icon">🏗️</span>
            3D Floor Plan View
          </h1>
          <p className="threed-subtitle">
            Interactive 3D visualization with {individualShapes.length} individual shapes and {mergedStructures.length} complex rooms
          </p>
        </div>
        <div className="header-right">
          <button className="nav-btn secondary" onClick={handleBackToWelcome}>
            <span className="btn-icon">🏠</span>
            Home
          </button>
          <button className="nav-btn primary" onClick={handleBackToPlanner}>
            <span className="btn-icon">📐</span>
            Back to Planner
          </button>
        </div>
      </div>

      {/* 3D Canvas */}
      <div className="threed-canvas-container">
        {totalShapes === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📦</div>
            <h3>No 3D Objects to Display</h3>
            <p>Start by adding some shapes in the floor planner to see them in 3D!</p>
            <button className="empty-action-btn" onClick={handleBackToPlanner}>
              <span className="btn-icon">➕</span>
              Add Shapes
            </button>
          </div>
        ) : (
          <Suspense fallback={<LoadingFallback />}>
            <Canvas shadows className="threed-canvas">
              <PerspectiveCamera makeDefault position={[10, 8, 10]} fov={60} />
              
              {/* Lighting */}
              <ambientLight intensity={0.6} />
              <directionalLight
                position={[10, 10, 5]}
                intensity={1}
                castShadow
                shadow-mapSize-width={2048}
                shadow-mapSize-height={2048}
                shadow-camera-far={50}
                shadow-camera-left={-10}
                shadow-camera-right={10}
                shadow-camera-top={10}
                shadow-camera-bottom={-10}
              />
              <pointLight position={[-10, 10, -10]} intensity={0.5} />
              
              {/* Environment */}
              <Environment preset="apartment" background />
              
              {/* Ground */}
              <Ground />
              
              {/* Individual 3D Shapes */}
              {individualShapes.map((shape, index) => (
                <Shape3D key={shape.id || index} shape={shape} index={index} />
              ))}
              
              {/* Merged Structures */}
              {mergedStructures.map((structure, index) => (
                <MergedStructure3D key={structure.id} structure={structure} index={index} />
              ))}
              
              {/* Controls */}
              <OrbitControls
                enablePan={true}
                enableZoom={true}
                enableRotate={true}
                minDistance={5}
                maxDistance={50}
                minPolarAngle={0}
                maxPolarAngle={Math.PI / 2.2}
                autoRotate={false}
                autoRotateSpeed={0.5}
              />
            </Canvas>
          </Suspense>
        )}
      </div>

      {/* Enhanced Controls Panel */}
      <div className="controls-panel">
        <div className="control-group">
          <h4>🎮 Controls</h4>
          <div className="control-item">
            <kbd>Mouse Drag</kbd>
            <span>Rotate view</span>
          </div>
          <div className="control-item">
            <kbd>Mouse Wheel</kbd>
            <span>Zoom in/out</span>
          </div>
          <div className="control-item">
            <kbd>Right Click + Drag</kbd>
            <span>Pan view</span>
          </div>
        </div>
        
        <div className="stats-group">
          <h4>📊 Floor Plan Statistics</h4>
          <div className="stat-item">
            <span className="stat-label">Individual Shapes:</span>
            <span className="stat-value">{individualShapes.length}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Complex Rooms:</span>
            <span className="stat-value">{mergedStructures.length}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Total Structures:</span>
            <span className="stat-value">{totalShapes}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Rectangles:</span>
            <span className="stat-value">{individualShapes.filter(s => s.type === 'rectangle').length}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Circles:</span>
            <span className="stat-value">{individualShapes.filter(s => s.type === 'circle').length}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Doors:</span>
            <span className="stat-value">{individualShapes.filter(s => s.type === 'door').length}</span>
          </div>
        </div>

        <div className="legend-group">
          <h4>🎨 Color Legend</h4>
          <div className="legend-item">
            <div className="color-box" style={{backgroundColor: '#4facfe'}}></div>
            <span>Rectangles</span>
          </div>
          <div className="legend-item">
            <div className="color-box" style={{backgroundColor: '#fa709a'}}></div>
            <span>Circles</span>
          </div>
          <div className="legend-item">
            <div className="color-box" style={{backgroundColor: '#a8edea'}}></div>
            <span>Triangles</span>
          </div>
          <div className="legend-item">
            <div className="color-box" style={{backgroundColor: '#ffd700'}}></div>
            <span>Doors</span>
          </div>
          <div className="legend-item">
            <div className="color-box" style={{backgroundColor: '#ff4757'}}></div>
            <span>Complex Rooms</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ThreeDView; 